import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getUserIPAddress } from "@/utils/ipUtils";

export const useFirstOrderDiscount = (userId: string | null) => {
  const [isEligible, setIsEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkEligibility = async () => {
      if (!userId) {
        setIsEligible(false);
        setLoading(false);
        return;
      }

      try {
        // Get user's IP address for eligibility check
        const ipAddress = await getUserIPAddress();
        
        const { data, error } = await supabase.rpc('is_eligible_for_first_order_discount', {
          user_id_param: userId,
          ip_address_param: ipAddress
        });

        if (error) throw error;

        setIsEligible(data || false);
      } catch (error) {
        console.error('Error checking first order discount eligibility:', error);
        setIsEligible(false);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [userId]);

  const applyFirstOrderDiscount = async () => {
    if (!isEligible) return null;

    try {
      const { data: coupon, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', 'WELCOME15')
        .eq('is_active', true)
        .single();

      if (error) throw error;

      if (coupon) {
        toast({
          title: "Welcome Discount Applied! 🎉",
          description: "15% off has been automatically applied to your first order",
        });
        return coupon;
      }
    } catch (error) {
      console.error('Error applying first order discount:', error);
      toast({
        title: "Discount Error",
        description: "There was an issue applying your welcome discount",
        variant: "destructive",
      });
    }

    return null;
  };

  return {
    isEligible,
    loading,
    applyFirstOrderDiscount,
  };
};